import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ComplianceBreadcrumb from "./ComplianceBreadcrumb";
import DeviceInventoryTable from "./DeviceInventoryTable";
import styles from "./complianceReportDetail.module.scss";
import {
  frSections,
  networkZones,
  dashboard,
  securityLevels,
  cveAlerts,
} from "./deviceInventoryData";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Refresh } from "@mui/icons-material";
import { downloadPDF } from "../hipaa/HipaaDetail";
const LevelCard = ({ level }) => {
  const ringStyle = {
    background: `conic-gradient(var(--${level.tone}) ${level.percent}%, #eef2f7 0)`,
  };

  const iconMap = {
    shield: <ShieldOutlinedIcon fontSize="small" />,
    lock: <LockOutlinedIcon fontSize="small" />,
    fingerprint: <FingerprintOutlinedIcon fontSize="small" />,
  };

  const statusClass =
    level.status === "Compliant"
      ? styles.statusCompliant
      : level.status === "Partial"
      ? styles.statusPartial
      : styles.statusProgress;

  return (
    <div className={styles.levelCard}>
      <div className={styles.levelRing} style={ringStyle}>
        <div className={styles.levelCenter}>
          <div
            className={`${styles.levelIcon} ${styles[level.toneBg]} ${
              styles[level.tone]
            }`}
          >
            {iconMap[level.icon] || iconMap.shield}
          </div>
          <span className={styles.levelValue}>{level.percent}%</span>
        </div>
      </div>
      <div className={styles.levelFooter}>
        <div className={styles.levelName}>{level.title}</div>
        <div className={styles.levelDesc}>{level.description}</div>
        <div className={styles.levelControls}>{level.controls}</div>
        <div className={`${styles.levelStatusPill} ${statusClass}`}>
          {level.status}
        </div>
      </div>
    </div>
  );
};

const ZoneCard = ({ zone }) => (
  <div className={`${styles.zoneCard} ${styles[zone.tone]}`}>
    <div className={styles.zoneHeader}>
      <div>
        <div className={styles.zoneName}>{zone.name}</div>
        <div className={styles.zoneDesc}>{zone.description}</div>
      </div>
      <Chip label={zone.badge} size="small" className={styles.zoneBadge} />
    </div>
    <Divider className={styles.zoneDivider} />
    <div className={styles.zoneMeta}>
      <div className={styles.metaItem}>
        <span className={styles.metaLabel}>Purdue Level</span>
        <span className={styles.metaValue}>{zone.purdue}</span>
      </div>
      <div className={styles.metaItem}>
        <span className={styles.metaLabel}>Security Level</span>
        <span className={styles.metaValue}>{zone.securityLevel}</span>
      </div>
      <div className={styles.metaItem}>
        <span className={styles.metaLabel}>Devices</span>
        <span className={styles.metaValue}>{zone.devices}</span>
      </div>
      <div className={styles.metaItem}>
        <span className={styles.metaLabel}>VLAN</span>
        <span className={styles.metaValue}>{zone.vlan}</span>
      </div>
    </div>
  </div>
);

const StatusDot = ({ status }) => (
  <span className={`${styles.statusDot} ${styles[status]}`} />
);

const FrSection = ({ section }) => (
  <div className={styles.frSection}>
    <div className={styles.frSectionHeader}>
      <div className={styles.frTitleBlock}>
        <span className={styles.frBadge}>{section.id}</span>
        <div className={styles.frTitle}>{section.title}</div>
      </div>
      <div className={styles.frTotal}>{section.total}</div>
    </div>
    <div className={styles.frTable}>
      <div className={styles.frTableHead}>
        <span>Requirement</span>
        <div className={styles.frStatusHead}>
          <span>SL-1</span>
          <span>SL-2</span>
          <span>SL-3</span>
          <span>SL-4</span>
        </div>
      </div>
      {section.requirements.map((req) => (
        <div key={req.label} className={styles.frRow}>
          <span className={styles.frLabel}>{req.label}</span>
          <div className={styles.frStatusRow}>
            {req.statuses.map((status, idx) => (
              <StatusDot key={`${req.label}-${idx}`} status={status} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CveAlertCard = ({ alert }) => (
  <div className={styles.cveCard}>
    <div className={styles.cveHeader}>
      <span
        className={`${styles.cveSeverity} ${
          styles[alert.severity.toLowerCase()]
        }`}
      >
        {alert.severity}
      </span>
      <div className={styles.cveScore}>
        <TrendingUpOutlinedIcon fontSize="small" />
        <span>CVSS {alert.score}</span>
      </div>
    </div>
    <div className={styles.cveId}>{alert.id}</div>
    <div className={styles.cveTitle}>{alert.title}</div>
    <div className={styles.cveMeta}>{alert.product}</div>

    <div className={styles.cveMetaRow}>
      <span className={styles.cveMetaLabel}>Purdue Level:</span>
      <span className={`${styles.cvePill} ${styles.cvePillPurple}`}>
        {alert.purdue}
      </span>
    </div>
    <div className={styles.cveMetaRow}>
      <span className={styles.cveMetaLabel}>Affected:</span>
      <span className={`${styles.cveMetaValue} ${styles.cveDanger}`}>
        {alert.affected}
      </span>
    </div>
    <div className={styles.cveMetaRow}>
      <span className={styles.cveMetaLabel}>Status:</span>
      <span
        className={`${styles.cvePill} ${
          alert.status === "unpatched"
            ? styles.cvePillDanger
            : alert.status === "mitigated"
            ? styles.cvePillSuccess
            : styles.cvePillWarning
        }`}
      >
        {alert.status}
      </span>
    </div>

    <Button variant="text" className={styles.cveAction}>
      View Details
    </Button>
  </div>
);

const ComplianceReportDetail = () => {
  return (
    <Box className={styles.page}>
      <Box className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "Compliance & Reporting", href: "/compliance-reports" },
            { label: "IEC 62443 Dashboard" },
          ]}
        />
        <Stack direction="row" gap={3}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<Refresh />}
            sx={{ borderRadius: 2, bgcolor: "white !important" }}
          >
            Refresh Data
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => downloadPDF("compliance-report")}
            sx={{ borderRadius: 2 }}
          >
            Export Report
          </Button>
        </Stack>
      </Box>
      <Stack id="report" gap={2}>
        <Card className={styles.headerCard}>
          <div className={styles.headerRow}>
            <div className={styles.titleBlock}>
              <div className={styles.iconBubble}>
                <ShieldOutlinedIcon />
              </div>
              <div>
                <Typography className={styles.reportTitle}>
                  {dashboard.title}
                </Typography>
                <div className={styles.reportMeta}>{dashboard.subtitle}</div>
              </div>
            </div>
            <div className={styles.scoreBlock}>
              <div className={styles.scoreLabel}>Overall Compliance Score</div>
              <div className={styles.scoreValue}>{dashboard.score}%</div>
              <div className={styles.scoreTarget}>
                Target: {dashboard.target}
              </div>
            </div>
          </div>
        </Card>

        {/* <Card className={styles.levelsCard}> */}
        <div className={styles.levelGrid}>
          {securityLevels.map((level) => (
            <LevelCard key={level.id} level={level} />
          ))}
        </div>
        {/* </Card> */}

        <Card className={styles.networkCard}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <LanOutlinedIcon className={styles.sectionIcon} />
              <Typography className={styles.sectionTitle}>
                Network Zones & Conduits Segmentation
              </Typography>
            </div>
            <div className={styles.sectionActions}>
              <Chip
                label="Purdue Model"
                size="small"
                className={styles.purdueChip}
              />
              <div className={styles.archBadge}>
                <SecurityOutlinedIcon fontSize="small" />
                <span>Level 5 Architecture</span>
              </div>
            </div>
          </div>

          <Divider />

          <div className={styles.zoneGrid}>
            {networkZones.map((zone) => (
              <ZoneCard key={zone.id} zone={zone} />
            ))}
          </div>

          <Divider />

          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.legendEncrypt}`} />
              Encrypted conduits
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.legendTrust}`} />
              Zero Trust enforced
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.legendIds}`} />
              IDS/IPS active
            </div>
          </div>
        </Card>

        <div className={styles.frCveGrid}>
          <Card className={styles.frCard}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleRow}>
                <BoltOutlinedIcon className={styles.sectionIcon} />
                <Typography className={styles.sectionTitle}>
                  Foundational Requirements (FR) Status Matrix
                </Typography>
              </div>
              <Chip
                label="SL-1 to SL-4"
                size="small"
                className={styles.purdueChip}
              />
            </div>
            <Divider />
            <div className={styles.frSections}>
              {frSections.map((section) => (
                <FrSection key={section.id} section={section} />
              ))}
            </div>
            <div className={styles.frLegend}>
              <div className={styles.legendItem}>
                <StatusDot status="complete" />
                Complete
              </div>
              <div className={styles.legendItem}>
                <StatusDot status="partial" />
                Partial
              </div>
              <div className={styles.legendItem}>
                <StatusDot status="in-progress" />
                In Progress
              </div>
              <div className={styles.legendItem}>
                <StatusDot status="not-started" />
                Not Started
              </div>
            </div>
          </Card>

          <Card className={styles.cvePanel}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleRow}>
                <WarningAmberOutlinedIcon className={styles.sectionIcon} />
                <Typography className={styles.sectionTitle}>
                  Critical CVE Alerts
                </Typography>
              </div>
              <Chip label="Live" size="small" className={styles.liveChip} />
            </div>
            <Divider />
            <div className={styles.cveList}>
              {cveAlerts.map((alert) => (
                <CveAlertCard key={alert.id} alert={alert} />
              ))}
            </div>
            <Button variant="contained" className={styles.manageButton}>
              Manage Vulnerabilities
            </Button>
          </Card>
        </div>

        <DeviceInventoryTable />
      </Stack>
    </Box>
  );
};

export default ComplianceReportDetail;
